/**
 * Format helpers — es-AR locale consistency.
 *
 * Bug fix (Founder QA 2026-09-16): pre-fix, the app used 5 different locales
 * for number/currency formatting:
 *   - es-CO (dashboard, conferences)  → $745,7 (1 decimal, comma)
 *   - default browser locale (wallet)  → $745.70 (2 decimals, dot)
 *   - es-ES (analytics)                → 1.234,56 (dot/comma swapped vs es-AR)
 *   - en-US (academy leaderboard)      → 1,234.56 (dot/comma en-US)
 *
 * The Founder decision (Pendiente #48) is to maintain es-AR — so ALL
 * user-facing numbers should use es-AR:
 *   - Decimal separator: comma (745,70)
 *   - Thousands separator: dot (1.610)
 *   - Currency: $X.XXX,XX USDT
 *
 * This module centralizes the format. The bug was surfaced when the dashboard
 * showed "Saldo disponible: $745,7 USDT" (es-CO, 1 decimal) but the wallet
 * showed "$745.70" (default, 2 decimals) — same value, different format,
 * looked like a bug to the user.
 *
 * §6 Anti-Invención: no business logic — pure formatting helpers. The values
 * come from the backend as-is; only the display changes.
 *
 * Spec ref: Pendiente #48 (Voseo — mantener es-AR) — by extension, also
 * maintain es-AR number formatting for consistency.
 */

const ES_AR_LOCALE = 'es-AR';

/**
 * Format a number (USDT or any currency amount) as es-AR with 2 decimals.
 *
 * Example: 745.7 → "745,70"
 *          1610 → "1.610,00"
 *          0.5  → "0,50"
 */
export function formatNumber(value: number | string | undefined | null, decimals = 2): string {
  const num = typeof value === 'string' ? parseFloat(value) : value;
  if (num === null || num === undefined || Number.isNaN(num)) return '0' + (decimals > 0 ? ',' + '0'.repeat(decimals) : '');
  return num.toLocaleString(ES_AR_LOCALE, {
    minimumFractionDigits: decimals,
    maximumFractionDigits: decimals,
  });
}

/**
 * Format a USDT amount with the `$` prefix and ` USDT` suffix, es-AR.
 *
 * Example: 745.7 → "$745,70 USDT"
 *          1610 → "$1.610,00 USDT"
 */
export function formatUSDT(value: number | string | undefined | null, decimals = 2): string {
  return `$${formatNumber(value, decimals)} USDT`;
}

/**
 * Format a credit amount (PM) — same as USDT but with ` cr.` suffix.
 *
 * Example: 100 → "100,00 cr."
 */
export function formatCredits(value: number | string | undefined | null, decimals = 2): string {
  return `${formatNumber(value, decimals)} cr.`;
}

/**
 * Format a date in es-AR locale with date + time.
 *
 * Example: 2026-09-16T12:48:41Z → "16/9/2026, 09:48:41"
 *
 * For more control, callers can pass `options` to override the defaults.
 */
export function formatDate(
  date: Date | string | number,
  options: Intl.DateTimeFormatOptions = { dateStyle: 'medium', timeStyle: 'short' },
): string {
  const d = typeof date === 'string' || typeof date === 'number' ? new Date(date) : date;
  return d.toLocaleString(ES_AR_LOCALE, options);
}

/**
 * Format a date with only date (no time).
 *
 * Example: 2026-09-16 → "16 de septiembre de 2026"
 */
export function formatDateOnly(date: Date | string | number): string {
  const d = typeof date === 'string' || typeof date === 'number' ? new Date(date) : date;
  return d.toLocaleDateString(ES_AR_LOCALE, { dateStyle: 'long' });
}
